import React, { useMemo, useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoAdd, IoClose, IoTrash, IoDownload, IoDocumentText } from "react-icons/io5";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import QuotationPDF from "./QuotationPDF";
import { useUser } from "../../../../contexts/UserContext";

const defaultItem = () => ({ id: Date.now().toString(), description: "", quantity: 1, unitPrice: 0, amount: 0 });

const QuotationBuilderModal = ({ isOpen, onClose, lead, showToast }) => {
    const { user } = useUser();
    const pdfRef = useRef(null);
    const [logoPreview] = useState(null);
    const companyName = user?.companyName || "Your Company Name";
    const companyShortName = user?.companyShortName || "";
    const companyAddress = [
        user?.raw?.houseNameOrNumber,
        user?.raw?.localityOrLandmark,
        user?.raw?.district,
        user?.raw?.state,
        user?.raw?.country,
        user?.raw?.pinCode
    ].filter(Boolean).join(", ") || "123 Business Rd, Suite 501, City, Country";
    const companyPhone = user?.phone || "";
    const companyEmail = user?.email || "";
    const gstNumber = user?.gstNumber || "";
    const companyLogo = user?.companyLogo || null;
    const companySeal = user?.companySeal || null;
    const [quoteNumber, setQuoteNumber] = useState(`QT-${new Date().getTime()}`);
    const [quoteDate, setQuoteDate] = useState(new Date().toISOString().slice(0, 10));
    const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
    const [items, setItems] = useState([defaultItem()]);
    const [taxRate, setTaxRate] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [additionalDescription, setAdditionalDescription] = useState("");
    const signature = "Authorized Signature";
    const [pdfLoading, setPdfLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            const nextQuote = `QT-${new Date().getTime()}`;
            setQuoteNumber(nextQuote);
            setQuoteDate(new Date().toISOString().slice(0, 10));
            setDueDate(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
            setItems([defaultItem()]);
            setTaxRate(0);
            setDiscount(0);
            setAdditionalDescription("");
            setErrors({});
        }
    }, [isOpen]);

    const customer = useMemo(() => ({
        name: lead?.name || "-",
        company: lead?.company || lead?.folderId?.name || "-",
        phone: lead?.phone || "-",
        email: lead?.email || "-",
        address: [lead?.houseNameOrNumber, lead?.localityOrLandmark, lead?.district, lead?.state, lead?.country, lead?.pinCode]
            .filter(Boolean)
            .join(", ") || "-",
    }), [lead]);

    const subtotal = useMemo(() => {
        return items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    }, [items]);

    const taxAmount = useMemo(() => {
        return (subtotal * Number(taxRate || 0)) / 100;
    }, [subtotal, taxRate]);

    const grandTotal = useMemo(() => {
        return subtotal + taxAmount - Number(discount || 0);
    }, [subtotal, taxAmount, discount]);

    const updateItem = (id, field, value) => {
        setItems((prev) => prev.map((item) => {
            if (item.id !== id) return item;
            const updated = { ...item, [field]: field === "description" ? value : Number(value) };
            updated.amount = Number(updated.quantity || 0) * Number(updated.unitPrice || 0);
            return updated;
        }));
    };

    const handleAddItem = () => {
        setItems((prev) => [...prev, defaultItem()]);
    };

    const handleRemoveItem = (id) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
    };

    const validate = () => {
        const nextErrors = {};
        items.forEach((item) => {
            if (!item.description.trim()) {
                nextErrors[`description_${item.id}`] = "Description is required";
            }
            if (!item.quantity || Number(item.quantity) <= 0) {
                nextErrors[`quantity_${item.id}`] = "Quantity must be greater than 0";
            }
            if (item.unitPrice === null || item.unitPrice === undefined || item.unitPrice === "" || Number(item.unitPrice) < 0) {
                nextErrors[`unitPrice_${item.id}`] = "Unit price is required";
            }
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const quotationData = {
        companyName,
        companyShortName,
        companyAddress,
        companyPhone,
        companyEmail,
        gstNumber,
        logoPreview: companyLogo || logoPreview,
        companySeal,
        customer,
        quoteNumber,
        quoteDate,
        dueDate,
        items: items.map(({ id, ...rest }) => rest),
        additionalDescription,
        subtotal,
        taxRate,
        taxAmount,
        discount,
        total: grandTotal,
        signature,
    };

    const handleGeneratePDF = async () => {
        if (!validate()) {
            showToast("Fix the fields before generating PDF.", "error");
            return;
        }

        setPdfLoading(true);
        try {
            const element = pdfRef.current;
            if (!element) {
                throw new Error("Quotation content not found");
            }

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: "a4" });
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save(`${quoteNumber || "quotation"}.pdf`);
            showToast("PDF generated successfully.", "success");
        } catch (err) {
            showToast(err?.message || "PDF generation failed.", "error");
        } finally {
            setPdfLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            document.body.style.overflow = "auto";
            return undefined;
        }

        document.body.style.overflow = "hidden";

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "auto";
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-black/40 px-4 py-8"
            >
                <motion.div
                    initial={{ y: -40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -40, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 24 }}
                    className="relative mx-auto flex h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[24px] bg-white p-3 shadow-xl shadow-black/5 ring-1 ring-slate-200 dark:bg-navy-900 dark:ring-white/10"
                >
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-5 top-5 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none dark:border-white/10 dark:bg-navy-900 dark:text-slate-100"
                    >
                        <IoClose className="h-4 w-4" />
                    </button>

                    <div className="flex min-h-0 flex-1 flex-col pr-2 sm:pr-3">
                        <div className="flex-1 min-h-0 overflow-y-auto space-y-3">
                            <div className="grid gap-3 lg:grid-cols-[1.1fr_0.9fr]">
                                <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-navy-800">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Bill To</p>
                                            <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Customer Details</h4>
                                        </div>
                                        <IoDocumentText className="h-6 w-6 text-brand-500" />
                                    </div>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="rounded-3xl bg-white p-3 text-sm text-slate-700 dark:bg-navy-900 dark:text-slate-200">
                                            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Name</p>
                                            <p className="mt-2 truncate text-base font-semibold">{customer.name}</p>
                                        </div>
                                        <div className="rounded-3xl bg-white p-3 text-sm text-slate-700 dark:bg-navy-900 dark:text-slate-200">
                                            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Company</p>
                                            <p className="mt-2 truncate text-base font-semibold">{customer.company}</p>
                                        </div>
                                        <div className="rounded-3xl bg-white p-3 text-sm text-slate-700 dark:bg-navy-900 dark:text-slate-200">
                                            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Phone</p>
                                            <p className="mt-2 truncate text-base font-semibold">{customer.phone}</p>
                                        </div>
                                        <div className="rounded-3xl bg-white p-3 text-sm text-slate-700 dark:bg-navy-900 dark:text-slate-200">
                                            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Email</p>
                                            <p className="mt-2 truncate text-base font-semibold">{customer.email}</p>
                                        </div>
                                        <div className="sm:col-span-2 rounded-3xl bg-white p-3 text-sm text-slate-700 dark:bg-navy-900 dark:text-slate-200">
                                            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Address</p>
                                            <p className="mt-2 truncate text-base font-semibold">{customer.address}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-navy-800">
                                    <div className="mb-3 flex items-center justify-between">
                                        <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Summary</p>
                                        <p className="font-semibold text-slate-900 dark:text-white">Totals</p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between rounded-3xl bg-white px-3 py-3 dark:bg-navy-900">
                                            <span className="text-sm text-slate-600 dark:text-slate-400">Subtotal</span>
                                            <span className="font-semibold text-slate-900 dark:text-white">₹{subtotal.toFixed(2)}</span>
                                        </div>
                                        <div className="grid gap-2">
                                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tax %</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={taxRate}
                                                onChange={(e) => setTaxRate(e.target.value)}
                                                className="w-full rounded-3xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Discount</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={discount}
                                                onChange={(e) => setDiscount(e.target.value)}
                                                className="w-full rounded-3xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between rounded-3xl bg-slate-900 px-3 py-3 text-white dark:bg-white/10 dark:text-white">
                                            <span className="text-sm">Grand Total</span>
                                            <span className="text-lg font-semibold">₹{grandTotal.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-navy-800">
                                <div className="mb-3 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Notes</p>
                                        <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Additional Description</h4>
                                    </div>
                                </div>
                                <textarea
                                    value={additionalDescription}
                                    onChange={(e) => setAdditionalDescription(e.target.value)}
                                    placeholder="Enter quotation description, project scope, notes or additional information..."
                                    className="max-h-[100px] w-full overflow-y-auto rounded-[16px] border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                />
                            </div>

                            <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-navy-800">
                                <div className="mb-3 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Items</p>
                                        <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Quotation Items</h4>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleAddItem}
                                        className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
                                    >
                                        <IoAdd className="h-4 w-4" /> Add Item
                                    </button>
                                </div>

                                <div className="overflow-x-auto rounded-[20px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-900">
                                    <div className="min-w-[780px]">
                                        <div className="grid grid-cols-[100px_1.5fr_110px_110px_70px] items-center gap-2 border-b border-slate-200 bg-slate-100 px-3 py-2 text-xs uppercase tracking-[0.25em] text-slate-500 dark:border-white/10 dark:bg-navy-950 dark:text-slate-400">
                                            <div>Qty</div>
                                            <div>Description</div>
                                            <div>Unit Price</div>
                                            <div>Amount</div>
                                            <div className="text-right">Delete</div>
                                        </div>

                                        <div className="max-h-[240px] space-y-2 overflow-y-auto custom-scrollbar p-3">
                                            <AnimatePresence initial={false}>
                                                {items.map((item) => (
                                                    <motion.div
                                                        key={item.id}
                                                        initial={{ opacity: 0, y: 12 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.95 }}
                                                        className="grid grid-cols-[100px_1.5fr_110px_110px_70px] items-start gap-2"
                                                    >
                                                        <div>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(e) => updateItem(item.id, "quantity", e.target.value)}
                                                                className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                                            />
                                                            {errors[`quantity_${item.id}`] && (
                                                                <p className="mt-1 text-xs text-red-600">{errors[`quantity_${item.id}`]}</p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <input
                                                                type="text"
                                                                value={item.description}
                                                                onChange={(e) => updateItem(item.id, "description", e.target.value)}
                                                                className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                                            />
                                                            {errors[`description_${item.id}`] && (
                                                                <p className="mt-1 text-xs text-red-600">{errors[`description_${item.id}`]}</p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={item.unitPrice}
                                                                onChange={(e) => updateItem(item.id, "unitPrice", e.target.value)}
                                                                className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                                            />
                                                            {errors[`unitPrice_${item.id}`] && (
                                                                <p className="mt-1 text-xs text-red-600">{errors[`unitPrice_${item.id}`]}</p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="rounded-3xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900 dark:border-white/10 dark:bg-navy-900 dark:text-white">
                                                                ₹{item.amount.toFixed(2)}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveItem(item.id)}
                                                                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600 transition hover:bg-red-200 dark:bg-red-900/20 dark:text-red-300"
                                                            >
                                                                <IoTrash className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-shrink-0 border-t border-slate-200 pt-4 dark:border-white/10">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={handleGeneratePDF}
                                    disabled={pdfLoading}
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                >
                                    {pdfLoading ? "Generating PDF..." : "Generate PDF"}
                                    <IoDownload className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                                >
                                    Close
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Generate a polished PDF and close the builder when you are done.</p>
                        </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
            <div
                ref={pdfRef}
                style={{
                    position: "absolute",
                    left: "-9999px",
                    top: 0,
                    width: "794px",
                    padding: 0,
                    pointerEvents: "none",
                    zIndex: -1,
                }}
            >
                <QuotationPDF quotation={{
                    ...quotationData,
                    customer,
                    subtotal,
                    taxAmount,
                    total: grandTotal,
                }} />
            </div>
        </AnimatePresence>
    );
};

export default QuotationBuilderModal;
